/**
 * Industry Template Selector Component
 * 
 * Displays available industry templates with:
 * - Industry cards with icons and descriptions
 * - Expected outcomes (response rates, timelines)
 * - Success stories from similar companies
 * - Framework mappings
 */
import React, { useState, useEffect } from 'react';
import { 

  Factory, 
  ShoppingBag, 
  Wheat, 
  Building, 
  Truck, 
  Monitor,
  Zap,
  Pill,
  Award,
  ChevronRight,
  Clock,
  TrendingUp,
  Users,
  CheckCircle,
  Star,
  FileText,
  Target,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { apiFetch } from '../../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for industries
const INDUSTRY_ICONS = {
  Factory: Factory,
  ShoppingBag: ShoppingBag,
  Wheat: Wheat,
  Building: Building,
  Truck: Truck,
  Monitor: Monitor,
  Zap: Zap,
  Pill: Pill,
  Award: Award
};

export default function IndustryTemplateSelector({ onSelectTemplate, selectedObligation }) {
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [industryDetail, setIndustryDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchIndustries();
    if (selectedObligation?.id) {
      fetchSuggestions(selectedObligation.id);
    }
  }, [selectedObligation]);

  const fetchIndustries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v1/industry-templates/industries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIndustries(data.industries || []);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (obligationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v1/industry-templates/suggestions/obligation/${obligationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchIndustryDetail = async (industryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v1/industry-templates/industries/${industryId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIndustryDetail(data);
        setSelectedIndustry(industryId);
      }
    } catch (error) {
      console.error('Error fetching industry detail:', error);
    }
  };

  const IconComponent = (iconName) => {
    const Icon = INDUSTRY_ICONS[iconName] || Factory;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If we have suggestions from an obligation, show them first
  if (suggestions.length > 0 && !selectedIndustry) {
    return (
      <div className="space-y-6">
        {/* Suggestions Header */}
        <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Sparkles className="text-purple-400" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                Recommended Templates
              </h3>
              <p className="text-sm text-purple-200/80">
                Based on your obligation, we recommend these industry-specific templates:
              </p>
            </div>
          </div>
        </div>

        {/* Suggested Templates */}
        <div className="grid md:grid-cols-2 gap-4">
          {suggestions.map((suggestion, idx) => (
            <SuggestionCard 
              key={idx}
              suggestion={suggestion}
              onSelect={() => onSelectTemplate(suggestion)}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="relative py-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#2F2F2F]" />
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-[#0A0A0A] text-sm text-[#6B6B6B]">
              Or browse all industries
            </span>
          </div>
        </div>

        {/* All Industries */}
        <div className="grid md:grid-cols-3 gap-4">
          {industries.map((industry) => (
            <IndustryCard 
              key={industry.id}
              industry={industry}
              IconComponent={IconComponent(industry.icon)}
              onClick={() => fetchIndustryDetail(industry.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Show industry detail with templates
  if (selectedIndustry && industryDetail) {
    return (
      <IndustryDetailView 
        industry={industryDetail}
        IconComponent={IconComponent(industryDetail.icon)}
        onBack={() => {
          setSelectedIndustry(null);
          setIndustryDetail(null);
        }}
        onSelectTemplate={onSelectTemplate}
      />
    );
  }

  // Default: Show all industries
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-xl font-bold text-white mb-2">Choose Your Industry</h2>
        <p className="text-sm text-[#6B6B6B]">
          Select your industry to see pre-built survey templates with expected outcomes
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {industries.map((industry) => (
          <IndustryCard 
            key={industry.id}
            industry={industry}
            IconComponent={IconComponent(industry.icon)}
            onClick={() => fetchIndustryDetail(industry.id)}
          />
        ))}
      </div>
    </div>
  );
}

// Industry Card Component
function IndustryCard({ industry, IconComponent, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left p-6 bg-[#111111] border border-[#1F1F1F] rounded-xl hover:border-purple-500/50 hover:bg-[#1F1F1F]/50 transition-all group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
          <IconComponent className="text-purple-400" size={24} />
        </div>
        <ChevronRight className="text-[#3F3F3F] group-hover:text-purple-400 transition-colors" size={20} />
      </div>
      
      <h3 className="text-base font-semibold text-white mb-2">{industry.name}</h3>
      <p className="text-xs text-[#6B6B6B] mb-4 line-clamp-2">{industry.description}</p>
      
      {/* Expected Outcomes */}
      <div className="space-y-2 pt-4 border-t border-[#1F1F1F]">
        <div className="flex items-center gap-2 text-xs">
          <TrendingUp size={12} className="text-emerald-400" />
          <span className="text-[#A0A0A0]">{industry.expectedOutcomes?.responseRate} response rate</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Clock size={12} className="text-blue-400" />
          <span className="text-[#A0A0A0]">{industry.expectedOutcomes?.completionTime}</span>
        </div>
      </div>
      
      {/* Frameworks */}
      <div className="flex flex-wrap gap-1 mt-4">
        {industry.frameworks?.slice(0, 3).map((fw, idx) => (
          <span key={idx} className="px-2 py-0.5 bg-[#1F1F1F] text-[10px] text-[#6B6B6B] rounded">
            {fw}
          </span>
        ))}
        {industry.frameworks?.length > 3 && (
          <span className="px-2 py-0.5 bg-[#1F1F1F] text-[10px] text-[#6B6B6B] rounded">
            +{industry.frameworks.length - 3}
          </span>
        )}
      </div>
    </button>
  );
}

// Suggestion Card Component
function SuggestionCard({ suggestion, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className="text-left p-5 bg-[#111111] border border-purple-500/30 rounded-xl hover:border-purple-500/50 hover:bg-purple-500/10 transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <FileText className="text-purple-400" size={18} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white">{suggestion.template?.name}</h4>
            <p className="text-xs text-purple-300">{suggestion.industry?.name}</p>
          </div>
        </div>
        <ArrowRight className="text-purple-400" size={18} />
      </div>
      
      <p className="text-xs text-[#6B6B6B] mb-3">{suggestion.template?.description}</p>
      
      <div className="flex items-center gap-4 text-xs">
        <span className="text-emerald-400 flex items-center gap-1">
          <TrendingUp size={12} />
          {suggestion.expectedOutcomes?.responseRate}
        </span>
        <span className="text-blue-400 flex items-center gap-1">
          <Clock size={12} />
          {suggestion.template?.estimatedTime}
        </span>
      </div>
    </button>
  );
}

// Industry Detail View Component
function IndustryDetailView({ industry, IconComponent, onBack, onSelectTemplate }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <button 
          onClick={onBack}
          className="text-sm text-[#6B6B6B] hover:text-purple-400 flex items-center gap-1"
        >
          ← Back to industries
        </button>
      </div>

      {/* Industry Header */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-purple-500/30 flex items-center justify-center">
            <IconComponent className="text-purple-400" size={28} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white mb-2">{industry.name}</h2>
            <p className="text-sm text-purple-200/80 mb-4">{industry.description}</p>
            
            {/* Expected Outcomes */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-[#0A0A0A]/50 rounded-lg">
                <p className="text-xs text-[#6B6B6B] mb-1">Response Rate</p>
                <p className="text-lg font-bold text-emerald-400">{industry.expectedOutcomes?.responseRate}</p>
              </div>
              <div className="p-3 bg-[#0A0A0A]/50 rounded-lg">
                <p className="text-xs text-[#6B6B6B] mb-1">Completion Time</p>
                <p className="text-lg font-bold text-blue-400">{industry.expectedOutcomes?.completionTime}</p>
              </div>
              <div className="p-3 bg-[#0A0A0A]/50 rounded-lg">
                <p className="text-xs text-[#6B6B6B] mb-1">Data Quality</p>
                <p className="text-lg font-bold text-amber-400">{industry.expectedOutcomes?.dataQualityScore}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Story */}
      {industry.successStory && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Star className="text-emerald-400 shrink-0" size={20} />
            <div>
              <h4 className="text-sm font-semibold text-emerald-300 mb-1">
                Success Story: {industry.successStory.company}
              </h4>
              <p className="text-sm text-emerald-200/80 mb-2">{industry.successStory.result}</p>
              <p className="text-xs text-emerald-200/60">✓ {industry.successStory.improvement}</p>
            </div>
          </div>
        </div>
      )}

      {/* Use Cases */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Target className="text-purple-400" size={16} />
          Use Cases
        </h4>
        <div className="flex flex-wrap gap-2">
          {industry.useCases?.map((useCase, idx) => (
            <span key={idx} className="px-3 py-1 bg-[#1F1F1F] text-xs text-[#A0A0A0] rounded-full">
              {useCase}
            </span>
          ))}
        </div>
      </div>

      {/* Templates */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4">Available Templates</h3>
        <div className="space-y-4">
          {industry.templates?.map((template) => (
            <TemplateCard 
              key={template.id}
              template={template}
              industry={industry}
              onSelect={() => onSelectTemplate({
                industry: { id: industry.id, name: industry.name, icon: industry.icon },
                template: template,
                frameworks: industry.frameworks,
                expectedOutcomes: industry.expectedOutcomes
              })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Template Card Component
function TemplateCard({ template, industry, onSelect }) {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-purple-500/30 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-base font-semibold text-white mb-1">{template.name}</h4>
          <p className="text-xs text-[#6B6B6B]">{template.description}</p>
        </div>
        <button
          onClick={onSelect}
          className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium flex items-center gap-2"
        >
          Use Template
          <ArrowRight size={14} />
        </button>
      </div>

      <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-4">
        <span className="flex items-center gap-1">
          <FileText size={12} />
          {template.questions?.length || 0} questions
        </span>
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {template.estimatedTime}
        </span>
      </div>

      {/* Question Preview */}
      <div className="space-y-2">
        <p className="text-xs text-[#6B6B6B]">Sample questions:</p>
        {template.questions?.slice(0, 3).map((q, idx) => (
          <div key={idx} className="flex items-start gap-2 text-xs">
            <span className="text-purple-400 shrink-0">{idx + 1}.</span>
            <span className="text-[#A0A0A0] line-clamp-1">{q.questionText}</span>
          </div>
        ))}
        {template.questions?.length > 3 && (
          <p className="text-xs text-[#6B6B6B] pl-4">+{template.questions.length - 3} more questions</p>
        )}
      </div>
    </div>
  );
}
